import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { userApi, api, money, formatApiErrorDetail } from "../lib/api";
import { track } from "../lib/analytics";
import { useCart } from "../context/CartContext";
import { useUser } from "../context/UserAuthContext";
import Seo from "../components/Seo";
import { toast } from "sonner";

const inp = "w-full px-4 py-3 text-sm rounded-sm border border-brand-gold-light/50 bg-white outline-none focus:border-brand-gold";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user, ready } = useUser();
  const nav = useNavigate();
  const [quote, setQuote] = useState(null);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", address: "", city: "", district: "", postal_code: "", invoice_type: "individual", company_name: "", tax_office: "", tax_no: "" });
  const [iframeToken, setIframeToken] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { track("begin_checkout", {}); }, []);
  useEffect(() => {
    if (items.length) api.post("/cart/quote", { items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })) })
      .then(({ data }) => setQuote(data)).catch(() => {});
  }, [items]);
  useEffect(() => { if (user) setForm((f) => ({ ...f, full_name: user.name || "", email: user.email || "", phone: user.phone || "", city: user.city || "" })); }, [user]);

  if (ready && !user) return (
    <section className="pt-40 pb-32 text-center">
      <Seo title="Ödeme" />
      <h1 className="font-serif text-3xl text-brand-green">Devam etmek için giriş yap</h1>
      <p className="text-brand-text/60 mt-3">Siparişini tamamlamak için hesabına giriş yapmalısın.</p>
      <div className="mt-6 flex gap-3 justify-center">
        <Link to="/giris?next=/odeme" className="bg-brand-gold text-brand-offwhite px-6 py-3 rounded-sm font-semibold">Giriş Yap</Link>
        <Link to="/kayit?next=/odeme" className="border border-brand-gold text-brand-green px-6 py-3 rounded-sm font-semibold">Kayıt Ol</Link>
      </div>
    </section>
  );

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!items.length) return;
    setLoading(true);
    try {
      const { data } = await userApi.post("/orders",
        { ...form, items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })) },
        { headers: { "Idempotency-Key": crypto.randomUUID() } });
      if (data.status === "paid") { clear(); track("purchase", { total: data.total }); nav(`/odeme-sonucu?oid=${data.order_number}`); return; }
      if (data.iframe_token) { setIframeToken(data.iframe_token); }
      else { toast.error("Ödeme başlatılamadı."); }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  if (iframeToken) {
    return (
      <section className="pt-28 pb-16 bg-brand-offwhite min-h-screen">
        <Seo title="Güvenli Ödeme" />
        <div className="max-w-3xl mx-auto px-6">
          <h1 className="font-serif text-3xl text-brand-green mb-4">Güvenli Ödeme</h1>
          <p className="text-brand-text/60 text-sm mb-4">Ödemeni PayTR güvenli formu üzerinden tamamla.</p>
          <iframe title="PayTR" src={`https://www.paytr.com/odeme/guvenli/${iframeToken}`} className="w-full min-h-[720px] border-0 rounded-sm bg-white" data-testid="paytr-iframe" />
        </div>
      </section>
    );
  }

  return (
    <section className="pt-32 pb-20 bg-brand-offwhite">
      <Seo title="Ödeme" />
      <div className="container-narrow px-6 lg:px-10">
        <h1 className="font-serif text-4xl md:text-5xl text-brand-green mb-10">Ödeme</h1>
        {items.length === 0 ? <p className="text-brand-text/60">Sepetin boş. <Link to="/magaza" className="text-brand-gold">Mağaza</Link></p> : (
        <form onSubmit={submit} data-testid="checkout-form" className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-brand-cream border border-brand-gold-light/40 rounded-sm p-6 space-y-4">
              <h3 className="font-serif text-2xl text-brand-green">Bilgiler</h3>
              <input required placeholder="Ad Soyad" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className={inp} data-testid="co-name" />
              <div className="grid sm:grid-cols-2 gap-4">
                <input required type="email" placeholder="E-posta" value={form.email} onChange={(e) => set("email", e.target.value)} className={inp} data-testid="co-email" />
                <input required placeholder="Telefon" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inp} data-testid="co-phone" />
              </div>
              <input placeholder="Adres" value={form.address} onChange={(e) => set("address", e.target.value)} className={inp} data-testid="co-address" />
              <div className="grid sm:grid-cols-3 gap-4">
                <input placeholder="İl" value={form.city} onChange={(e) => set("city", e.target.value)} className={inp} />
                <input placeholder="İlçe" value={form.district} onChange={(e) => set("district", e.target.value)} className={inp} />
                <input placeholder="Posta Kodu" value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} className={inp} />
              </div>
              <div>
                <label className="text-sm font-medium text-brand-text">Fatura Tipi</label>
                <select value={form.invoice_type} onChange={(e) => set("invoice_type", e.target.value)} className={inp + " mt-1"} data-testid="co-invoice-type">
                  <option value="individual">Bireysel</option>
                  <option value="corporate">Kurumsal</option>
                </select>
              </div>
              {form.invoice_type === "corporate" && (
                <div className="grid sm:grid-cols-3 gap-4">
                  <input placeholder="Firma Adı" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} className={inp} />
                  <input placeholder="Vergi Dairesi" value={form.tax_office} onChange={(e) => set("tax_office", e.target.value)} className={inp} />
                  <input placeholder="Vergi No" value={form.tax_no} onChange={(e) => set("tax_no", e.target.value)} className={inp} />
                </div>
              )}
            </div>
          </div>
          <div className="bg-brand-green text-brand-offwhite rounded-sm p-8 h-fit">
            <h3 className="font-serif text-2xl mb-5">Sipariş Özeti</h3>
            {items.map((i) => <div key={i.product_id} className="flex justify-between text-sm mb-2"><span className="text-brand-offwhite/80">{i.name} × {i.quantity}</span><span>{money(i.price * i.quantity)}</span></div>)}
            <div className="border-t border-brand-offwhite/15 mt-3 pt-3 flex justify-between text-sm"><span className="text-brand-offwhite/70">KDV dahil</span><span>{money(quote?.vat_total || 0)}</span></div>
            <div className="flex justify-between font-serif text-xl mt-2"><span>Toplam</span><span data-testid="co-total">{money(quote?.total ?? subtotal)}</span></div>
            <button type="submit" disabled={loading} data-testid="co-submit" className="w-full mt-6 bg-brand-gold text-brand-offwhite hover:bg-brand-gold-light hover:text-brand-green transition-colors py-3.5 rounded-sm font-semibold disabled:opacity-60">{loading ? "İşleniyor..." : "Ödemeye Geç"}</button>
          </div>
        </form>
        )}
      </div>
    </section>
  );
}
